import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtLogCompact } from "@/lib/tz";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MessageSquare, Loader2, Send, History } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "#bf3b4f";
export const SEGMENTS = (t) => Math.max(1, Math.ceil((t || "").length / 160));

/** Compose a one-off SMS to a single person and see their message history.
 *  `recipient` = { type: staff|participant|contact|stakeholder, id, name, mobile }. */
export function SendSmsDialog({ open, onOpenChange, recipient, onSent }) {
  const [message, setMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !recipient) return;
    setMessage("");
    api.smsTemplates().then((r) => setTemplates(r.templates || [])).catch(() => setTemplates([]));
    api.smsLog({ recipient_type: recipient.type, recipient_id: recipient.id, limit: 25 })
      .then((r) => setHistory(r.messages || [])).catch(() => setHistory([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipient?.type, recipient?.id]);

  const preview = useMemo(() => {
    const first = (recipient?.name || "").trim().split(" ")[0];
    return (message || "").replace(/\{first_name\}/g, first).replace(/\{full_name\}/g, recipient?.name || "");
  }, [message, recipient]);

  const send = async () => {
    if (!message.trim()) { toast.error("Type a message first."); return; }
    setSending(true);
    try {
      const res = await api.sendSms({
        recipients: [{ type: recipient.type, id: recipient.id, mobile: recipient.mobile, name: recipient.name }],
        message: message.trim(),
      });
      if (res.failed) toast.error(res.results?.[0]?.error || "The message could not be sent.");
      else toast.success(`Message sent to ${recipient.name}.`);
      setMessage("");
      api.smsLog({ recipient_type: recipient.type, recipient_id: recipient.id, limit: 25 })
        .then((r) => setHistory(r.messages || [])).catch(() => {});
      onSent?.(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the message.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] flex flex-col" data-testid="sms-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" style={{ color: CRIMSON }} />
            Send SMS to {recipient?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto space-y-3 pr-1">
          <div className="text-[11px] text-slate-500">
            To <span className="font-medium text-slate-700">{recipient?.mobile || "no mobile on file"}</span> · sent from
            <span className="font-medium text-slate-700"> Prydes</span> — recipients can't reply to this number.
          </div>
          {recipient?.guardianOnly && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800" data-testid="sms-guardian-only-warning">
              <b>Contact via guardian only.</b> This participant should be contacted through their legal guardian. Text the guardian from their contact instead, unless you have a reason to text the participant directly.
            </div>
          )}

          {templates.length > 0 && (
            <Select onValueChange={(v) => setMessage(templates.find((t) => t.id === v)?.body || "")}>
              <SelectTrigger className="h-9 text-xs" data-testid="sms-template-picker">
                <SelectValue placeholder="Use a saved template (optional)" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id} className="text-xs">{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
            className="text-sm" data-testid="sms-message"
            placeholder="Type your message… use {first_name} to greet them by name." />
          <div className="flex items-center justify-between text-[10px] text-slate-400">
            <span>{message.length} characters · {SEGMENTS(message)} SMS segment{SEGMENTS(message) === 1 ? "" : "s"}</span>
            {message.includes("{") && <span className="text-slate-500">Preview: “{preview.slice(0, 60)}{preview.length > 60 ? "…" : ""}”</span>}
          </div>

          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 mb-1.5">
              <History className="h-3.5 w-3.5" /> Message history
            </div>
            {history === null && <div className="text-[11px] text-slate-400">Loading…</div>}
            {history?.length === 0 && <div className="text-[11px] text-slate-400" data-testid="sms-history-empty">No messages sent to this person yet.</div>}
            <div className="space-y-1.5">
              {(history || []).map((m) => (
                <div key={m.id} className="rounded-md border border-slate-200 px-2.5 py-1.5" data-testid={`sms-history-row-${m.id}`}>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                    <Badge className={`text-[9px] py-0 border ${m.status === "Sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{m.status}</Badge>
                    {m.kind !== "manual" && <Badge className="text-[9px] py-0 bg-slate-100 text-slate-600 border border-slate-200">{m.kind === "bulk" ? "Bulk" : "Automated"}</Badge>}
                    {fmtLogCompact(m.at)} · {m.sent_by_name || m.source || "System"}
                  </div>
                  <div className="text-[11px] text-slate-700 mt-0.5 whitespace-pre-wrap">{m.body}</div>
                  {m.error && <div className="text-[10px] text-red-600 mt-0.5">{m.error}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="sms-cancel">Close</Button>
          <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={send} data-testid="sms-send"
            disabled={sending || !message.trim() || !recipient?.mobile}>
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Send SMS
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Small inline trigger — drops an "SMS" button next to a person anywhere. */
export function SmsButton({ type, id, name, mobile, guardianOnly = false, size = "sm", label = "SMS", className = "" }) {
  const [open, setOpen] = useState(false);
  if (!mobile) return null;
  return (
    <>
      <Button variant="outline" size={size} className={`h-7 text-[11px] ${className}`}
        onClick={() => setOpen(true)} data-testid={`sms-btn-${type}-${id || "manual"}`}>
        <MessageSquare className="h-3 w-3 mr-1" />{label}
      </Button>
      <SendSmsDialog open={open} onOpenChange={setOpen} recipient={{ type, id, name, mobile, guardianOnly }} />
    </>
  );
}
